<template>
    <div class="date-select">
        <div class="date-select__calendar">
            <!-- Date Navigation -->
            <div class="date-select__days-container">
                <button class="date-select__nav-button" @click="scrollDays(-1)">
                    <ChevronLeftIcon />
                </button>
                <div class="date-select__days-wrapper">
                    <div class="date-select__days">
                        <button
                            v-for="day in preparedCalendarDays"
                            :key="day.date"
                            class="date-select__day"
                            :class="{
                                'date-select__day--selected':
                                    selectedDay &&
                                    day.date === selectedDay.date,
                                'date-select__day--today': day.date === today,
                            }"
                            :disabled="
                                selectedDay && day.date === selectedDay.date
                            "
                            @click="handleDayClick(day.date)"
                        >
                            <div class="date-select__day-label">
                                {{ day.dayFormatted }}

                                <span
                                    v-if="day.isMarked"
                                    class="date-select__day-marker"
                                ></span>
                            </div>
                            <div class="date-select__day-number">
                                {{ day.dateFormatted }}
                            </div>
                        </button>
                    </div>
                </div>
                <button
                    class="date-select__nav-button"
                    :disabled="isRightDisabled"
                    @click="scrollDays(1)"
                >
                    <ChevronRightIcon />
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import moment from 'moment'

import ChevronLeftIcon from '../icons/ChevronLeftIcon.vue'
import ChevronRightIcon from '../icons/ChevronRightIcon.vue'

const MAX_VISIBLE_DAYS = window.innerWidth < 896 ? 4 : 7 // Configurable visible days

export default {
    name: 'DateSelect',
    components: {
        ChevronLeftIcon,
        ChevronRightIcon,
    },
    props: {
        value: {
            type: String,
            default: null,
        },
        markedDays: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            preparedCalendarDays: [],
            spinnerColor: process.env.VUE_APP_COLOR_PRIMARY,
            visibleStartIndex: 0, // Calculated dynamically
        }
    },
    computed: {
        isRightDisabled() {
            // Check if the last day is today
            const lastDay = this.preparedCalendarDays[
                this.preparedCalendarDays.length - 1
            ]
            return lastDay && lastDay.date === this.today
        },
        today() {
            return moment().format('YYYY-MM-DD')
        },
        selectedDay() {
            // Find the selected day based on the value prop
            return this.preparedCalendarDays.find(
                day => day.date === this.value
            )
        },
    },
    watch: {
        value(newValue) {
            if (!this.selectedDay) {
                this.init(
                    moment(newValue).subtract(MAX_VISIBLE_DAYS - 1, 'days')
                )
            }
        },
        markedDays() {
            this.scrollDays(0)
        },
    },
    created() {
        moment.locale(this.$i18n.locale)
        this.init(moment(this.value).subtract(MAX_VISIBLE_DAYS - 1, 'days'))
    },
    methods: {
        init(startDate) {
            this.preparedCalendarDays = Array.from(
                { length: MAX_VISIBLE_DAYS },
                (_, i) => {
                    const date = startDate.clone().add(i, 'days')

                    return {
                        moment: date,
                        date: date.format('YYYY-MM-DD'),
                        dayFormatted: date.format('ddd'),
                        dateFormatted: date
                            .format('ll')
                            .replace(/[\s\d]+$/, '')
                            .replace(/,\s*$/, ''),
                        isMarked:
                            this.markedDays &&
                            this.markedDays.some(markedDay =>
                                moment(markedDay).isSame(date, 'day')
                            ),
                    }
                }
            )
        },
        handleDayClick(date) {
            this.$emit('input', date)
        },
        scrollDays(direction) {
            const firstDay = moment(this.preparedCalendarDays[0].date)
            const newStartDate = firstDay.clone().add(direction, 'days')

            this.init(newStartDate)
        },
    },
}
</script>

<i18n>
{
    "en": {
        "calendarWeek": "Calendar week {0}"
    },
    "de": {
        "calendarWeek": "Kalenderwoche {0}"
    },
    "fr": {
        "calendarWeek": "Semaine Civile {0}"
    },
    "it": {
        "calendarWeek": "Settimana di Calendario {0}"
    }
}
</i18n>

<style lang="scss" scoped>
.date-select {
    width: 100%;

    &__day-marker {
        position: absolute;
        top: 8px;
        width: 8px;
        height: 8px;
        background-color: $color-danger;
        border-radius: 4px;
    }

    &__calendar {
        display: flex;
        justify-content: space-between;
        flex-wrap: wrap;
        gap: 1rem;

        @include respond-to('for-mobile-down') {
            flex-direction: column;
        }
    }

    &__header {
        h1 {
            font-size: 28px;
            color: $color-primary;
        }
    }

    &__days-container {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        background-color: #fff;
        border-radius: 8px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    }

    &__days-wrapper {
        flex-grow: 1;
        width: 100%;
        height: 100%;

        & > * {
            height: 100%;
        }
    }

    &__days {
        display: flex;
        height: 100%;
    }

    &__day {
        flex: 1;
        position: relative;
        min-width: 70px;
        padding: 8px;
        border: none;
        background-color: #fff;
        font-family: 'IBM Plex Sans', sans-serif;
        font-size: 16px;
        color: $color-primary;
        text-align: center;
        transition: background 0.1s;
        white-space: nowrap;
        cursor: pointer;

        &:not(:disabled) {
            &:hover {
                background-color: darken(#fff, 1%);
            }
        }

        &--today {
            color: $color-blue-light;
        }

        &--selected {
            background-color: $color-primary;
            color: $color-primary-contrast;
        }

        &-label {
            font-weight: bold;
        }

        &-number {
            font-size: $fs-small;
        }
    }

    &__nav-button {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;
        min-height: 54px;
        padding: 0.5rem;
        background-color: #fff;
        border: none;
        color: $color-primary;
        transition: all 0.1s;
        cursor: pointer;

        &:not(:disabled) {
            &:hover {
                background-color: darken(#fff, 1%);
            }
        }

        &:disabled {
            color: $color-primary-lighter;
            cursor: not-allowed;
        }

        &:first-of-type {
            border-right: 1px solid rgba(0, 0, 0, 0.1);
        }

        &:last-of-type {
            border-left: 1px solid rgba(0, 0, 0, 0.1);
        }
    }
}
</style>
